import React, { useState } from 'react';
import {
  UniversityDemandAnalytics,
  DemandPlanningSettings,
  DemandPlanningStatus,
} from '../../../types';
import {
  Sparkles,
  Bus,
  Calendar,
  ArrowRight,
  MapPin,
  Clock,
  Layers,
} from 'lucide-react';

interface RouteOpportunitiesViewProps {
  universities: UniversityDemandAnalytics[];
  settings: DemandPlanningSettings;
  onUpdateStatus?: (
    uniId: string,
    status: DemandPlanningStatus,
    note?: string
  ) => Promise<void>;
  onScheduleTripClick?: (universityId: string) => void;
}

export const RouteOpportunitiesView: React.FC<RouteOpportunitiesViewProps> = ({
  universities,
  settings,
  onUpdateStatus,
  onScheduleTripClick,
}) => {
  const [selectedUniForStatus, setSelectedUniForStatus] = useState<UniversityDemandAnalytics | null>(
    null
  );
  const [targetStatus, setTargetStatus] = useState<DemandPlanningStatus>('ROUTE_PLANNING');
  const [statusNote, setStatusNote] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Categorize opportunities
  const highDemand = universities.filter(
    (u) => u.activeStudents >= settings.highDemandThreshold
  );
  const mediumDemand = universities.filter(
    (u) =>
      u.activeStudents >= settings.minStudentsForRoute &&
      u.activeStudents < settings.highDemandThreshold
  );
  const monitoringDemand = universities.filter(
    (u) => u.activeStudents < settings.minStudentsForRoute
  );

  const handleStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUniForStatus || !onUpdateStatus) return;
    setIsSubmitting(true);
    try {
      await onUpdateStatus(selectedUniForStatus.universityId, targetStatus, statusNote);
      setSelectedUniForStatus(null);
      setStatusNote('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Overview Intro Banner */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-6 rounded-2xl shadow-md">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="text-amber-400" size={22} />
          <h2 className="text-xl font-bold">ذكاء العمليات وفرص تدشين الخطوط (Fleet & Route Planner)</h2>
        </div>
        <p className="text-blue-100 text-sm max-w-3xl leading-relaxed">
          تحليل مؤشرات الجدوى التشغيلية لمساعدة فريق العمليات في اتخاذ قرار فتح خطوط سفر جديدة للحافلات (سعة {settings.vehicleCapacity} مقعداً)،
          بناءً على الكثافة الطلابية، متوسط أيام الحضور، وتجمع مناطق الإركاب في الإسكندرية.
        </p>

        <div className="flex flex-wrap gap-4 mt-4 text-xs font-semibold">
          <div className="bg-white/10 px-3 py-1.5 rounded-lg border border-white/20">
            الحد الأدنى لتقييم الخط: <strong>{settings.minStudentsForRoute} طلاب</strong>
          </div>
          <div className="bg-white/10 px-3 py-1.5 rounded-lg border border-white/20">
            عتبة الإقبال المرتفع: <strong>{settings.highDemandThreshold}+ طلاب</strong>
          </div>
          <div className="bg-white/10 px-3 py-1.5 rounded-lg border border-white/20">
            سعة الحافلة القياسية: <strong>{settings.vehicleCapacity} مقعداً</strong>
          </div>
        </div>
      </div>

      {/* 1. High Demand Opportunities */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></span>
            <h3 className="font-extrabold text-slate-800 text-base">
              🔥 خطوط ذات أولوية قصوى وجدوى تشغيلية مؤكدة ({highDemand.length})
            </h3>
          </div>
          <span className="text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold px-2.5 py-1 rounded-full">
            تجاوزت {settings.highDemandThreshold} طالباً
          </span>
        </div>

        {highDemand.length === 0 ? (
          <div className="p-6 bg-slate-50 border rounded-2xl text-center text-slate-500 text-sm">
            لا توجد جامعات في فئة الطلب المرتفع حالياً
          </div>
        ) : (
          <div className="opportunity-cards-grid">
            {highDemand.map((u) => {
              const top3Areas = u.topAreas.slice(0, 3);
              const concentrationPct = top3Areas.reduce((acc, c) => acc + c.percentage, 0);

              return (
                <div key={u.universityId} className="opportunity-card high">
                  <div>
                    <div className="opportunity-card-header">
                      <div>
                        <h4 className="font-extrabold text-slate-900 text-base mb-0.5">
                          {u.universityName}
                        </h4>
                        <span className="text-xs text-slate-500">محافظة {u.governorate}</span>
                      </div>
                      <span className="opportunity-badge high">
                        مؤهل للتشغيل الفوري ✅
                      </span>
                    </div>

                    {/* Numbers Grid */}
                    <div className="grid grid-cols-2 gap-2 p-3 bg-white/80 rounded-xl border border-slate-100 text-xs mb-3">
                      <div>
                        <span className="text-slate-500 block">الطلاب المسجلين:</span>
                        <strong className="text-base text-slate-900 font-mono">
                          {u.activeStudents} طالب
                        </strong>
                      </div>
                      <div>
                        <span className="text-slate-500 block">حجم الحضور الشهري:</span>
                        <strong className="text-base text-emerald-700 font-mono">
                          {u.totalAttendanceDemand.toLocaleString('ar-EG')} يوم
                        </strong>
                      </div>
                      <div>
                        <span className="text-slate-500 block">المعادل من الأسطول:</span>
                        <strong className="text-slate-800 flex items-center gap-1 font-mono">
                          <Bus size={14} className="text-blue-600" />
                          <span>~{u.hiaceEquivalent} حافلة</span>
                        </strong>
                      </div>
                      <div>
                        <span className="text-slate-500 block">متوسط الحضور:</span>
                        <strong className="text-slate-800 font-mono">
                          {u.avgAttendanceDays} يوم/شهر
                        </strong>
                      </div>
                    </div>

                    {/* Proposed Pickup Corridor */}
                    <div className="opportunity-corridor">
                      <div className="opportunity-corridor-title">
                        <MapPin size={14} className="text-blue-600" />
                        <span>محور التجمع المقترح في الإسكندرية ({concentrationPct}% من الطلاب):</span>
                      </div>
                      <div className="corridor-chips">
                        {top3Areas.map((a, i) => (
                          <span key={a.areaId} className="corridor-chip">
                            {i + 1}. {a.areaName} ({a.studentCount} طلاب)
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-900 text-xs leading-relaxed mb-4 border border-emerald-200">
                      <strong>التوصية التشغيلية: </strong>
                      {u.recommendation}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    <button
                      type="button"
                      className="btn-primary text-xs py-2 px-3 flex-1 flex items-center justify-center gap-1.5"
                      onClick={() => onScheduleTripClick && onScheduleTripClick(u.universityId)}
                    >
                      <Calendar size={14} />
                      <span>جدولة رحلة في النظام</span>
                    </button>

                    <button
                      type="button"
                      className="btn-admin-outline text-xs py-2 px-3"
                      onClick={() => {
                        setSelectedUniForStatus(u);
                        setTargetStatus(u.planningStatus);
                      }}
                      title="تحديث حالة التخطيط والملاحظات التشغيلية"
                    >
                      <span>تحديث الحالة</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. Medium Demand (Ready for Feasibility) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <h3 className="font-extrabold text-slate-800 text-base">
              ⚠️ خطوط قيد التنسيق والدراسة ({mediumDemand.length})
            </h3>
          </div>
          <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200 font-bold px-2.5 py-1 rounded-full">
            بين {settings.minStudentsForRoute} و {settings.highDemandThreshold - 1} طالباً
          </span>
        </div>

        {mediumDemand.length === 0 ? (
          <div className="p-5 bg-slate-50 border rounded-2xl text-center text-slate-500 text-xs">
            لا توجد جامعات في هذه الفئة حالياً
          </div>
        ) : (
          <div className="opportunity-cards-grid">
            {mediumDemand.map((u) => {
              const top3Areas = u.topAreas.slice(0, 3);

              return (
                <div key={u.universityId} className="opportunity-card medium">
                  <div>
                    <div className="opportunity-card-header">
                      <div>
                        <h4 className="font-extrabold text-slate-900 text-sm">
                          {u.universityName}
                        </h4>
                        <span className="text-xs text-slate-500">{u.governorate}</span>
                      </div>
                      <span className="opportunity-badge medium">
                        مكتمل النصاب الأدنى
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-xs p-2.5 bg-white rounded-lg border border-slate-100 mb-3">
                      <div>
                        <span className="text-slate-500 block">العدد الحالي</span>
                        <strong className="text-slate-900 font-mono text-sm">
                          {u.activeStudents} طالب
                        </strong>
                      </div>
                      <div>
                        <span className="text-slate-500 block">أيام الحضور</span>
                        <strong className="text-slate-900 font-mono text-sm">
                          {u.totalAttendanceDemand} يوم
                        </strong>
                      </div>
                      <div>
                        <span className="text-slate-500 block">المعادل من الأسطول</span>
                        <strong className="text-slate-900 font-mono text-sm">
                          ~{u.hiaceEquivalent} حافلة
                        </strong>
                      </div>
                    </div>

                    <div className="text-xs text-slate-600 mb-3">
                      <strong>أهم مناطق الطلب: </strong>
                      {top3Areas.map((a) => `${a.areaName} (${a.studentCount})`).join('، ')}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    <button
                      type="button"
                      className="btn-admin-action-primary text-xs py-1.5 px-3 flex-1 flex items-center justify-center gap-1"
                      onClick={() => onScheduleTripClick && onScheduleTripClick(u.universityId)}
                    >
                      <span>دراسة الجدولة</span>
                      <ArrowRight size={12} />
                    </button>
                    <button
                      type="button"
                      className="btn-admin-outline text-xs py-1.5 px-2.5"
                      onClick={() => {
                        setSelectedUniForStatus(u);
                        setTargetStatus(u.planningStatus);
                      }}
                    >
                      حالة
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. Monitoring Demand */}
      {monitoringDemand.length > 0 && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-slate-500" />
            <h4 className="font-bold text-slate-800 text-xs">
              وجهات قيد المراقبة وتجميع الطلبات ({monitoringDemand.length})
            </h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {monitoringDemand.map((u) => (
              <span
                key={u.universityId}
                className="bg-white px-2.5 py-1 rounded-lg border text-xs text-slate-700 flex items-center gap-1.5"
              >
                <span>{u.universityName}</span>
                <span className="font-mono font-bold text-slate-900">
                  ({u.activeStudents} طلاب)
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {selectedUniForStatus && (
        <div
          className="demand-modal-overlay"
          onClick={() => setSelectedUniForStatus(null)}
        >
          <div className="demand-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="demand-modal-header">
              <h3 className="demand-modal-title">
                <Layers size={20} className="text-blue-600" />
                <span>تحديث حالة التخطيط التشغيلي — {selectedUniForStatus.universityName}</span>
              </h3>
              <button
                className="demand-modal-close-btn"
                onClick={() => setSelectedUniForStatus(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div className="demand-form-group">
                <label className="demand-form-label">حالة التخطيط الحالية</label>
                <select
                  value={targetStatus}
                  onChange={(e) => setTargetStatus(e.target.value as DemandPlanningStatus)}
                  className="demand-form-select"
                >
                  <option value="NEW">جديد (NEW)</option>
                  <option value="MONITORING">قيد المراقبة (MONITORING)</option>
                  <option value="HIGH_DEMAND">إقبال مرتفع (HIGH_DEMAND)</option>
                  <option value="ROUTE_PLANNING">قيد التخطيط للخط (ROUTE_PLANNING)</option>
                  <option value="ROUTE_CREATED">تم فتح رحلة وجدولتها (ROUTE_CREATED)</option>
                  <option value="ACTIVE_ROUTE">خط نشط ومستمر (ACTIVE_ROUTE)</option>
                  <option value="CLOSED">مغلق مؤقتاً (CLOSED)</option>
                </select>
              </div>

              <div className="demand-form-group">
                <label className="demand-form-label">ملاحظات العمليات والتنسيق</label>
                <textarea
                  rows={3}
                  placeholder="مثال: يفضل تشغيل خط صباحي 6:30 ص بنقاط تجمع سموحة وسيدي جابر..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  className="demand-form-textarea"
                />
              </div>

              <div className="demand-modal-actions">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setSelectedUniForStatus(null)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'جارٍ الحفظ...' : 'حفظ التحديث'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
